/**
 * Pure-function helpers to classify the relationship between two crops
 * for drag-time companion / antagonist preview.
 *
 * Symmetric to the backend's `is_antagonist` / companion logic in
 * `/app/backend/grid_utils.py`. Kept on the frontend so we can preview
 * the relation while dragging without an API round-trip.
 */

/** Lower-case set of aliases for a crop: common_name + rotation_family + plural. */
function aliases(crop) {
    if (!crop) return new Set();
    const name = (crop.crop_name || crop.common_name || "").trim().toLowerCase();
    const fam = (crop.rotation_family || "").trim().toLowerCase();
    return new Set([name, fam, fam ? `${fam}s` : ""].filter(Boolean));
}

function listSet(list) {
    return new Set((list || []).map((s) => (s || "").trim().toLowerCase()).filter(Boolean));
}

function setsIntersect(a, b) {
    for (const v of a) if (b.has(v)) return true;
    return false;
}

/**
 * Returns "antagonist" | "companion" | "neutral".
 * Antagonist takes precedence over companion when both lists overlap.
 */
export function relateCrops(a, b) {
    if (!a || !b || a.id === b.id) return "neutral";
    const aAliases = aliases(a);
    const bAliases = aliases(b);
    const aAnt = listSet(a.antagonists);
    const bAnt = listSet(b.antagonists);
    if (setsIntersect(aAnt, bAliases) || setsIntersect(bAnt, aAliases)) return "antagonist";
    const aComp = listSet(a.companions);
    const bComp = listSet(b.companions);
    if (setsIntersect(aComp, bAliases) || setsIntersect(bComp, aAliases)) return "companion";
    return "neutral";
}

/**
 * Pick the "worst" (most user-relevant) relation across multiple neighbours.
 * antagonist > companion > neutral.
 */
export function strongestRelation(relations) {
    if (relations.includes("antagonist")) return "antagonist";
    if (relations.includes("companion")) return "companion";
    return "neutral";
}

/**
 * Returns the four orthogonal neighbour cell coordinates (1-indexed) of (row, col),
 * filtered to those inside the grid.
 */
export function orthogonalNeighbors(row, col, rows, cols) {
    const out = [];
    if (row > 1) out.push([row - 1, col]);
    if (row < rows) out.push([row + 1, col]);
    if (col > 1) out.push([row, col - 1]);
    if (col < cols) out.push([row, col + 1]);
    return out;
}
